import React, { createContext, useState, useContext, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import loginService, { refresh } from "../../services/authServices";
import { getAuthContextFromToken } from "../../utils/jwtUtils";

const AuthContext = createContext();
const ADMIN_EMAIL_STORAGE_KEY = "adminUserEmail";

const EMPTY_AUTH_CONTEXT = { role: null, loginId: null };
const FRESH_LOGIN_REFRESH_SKIP_MS = 5000;

const readStoredEmail = () => {
  const saved = sessionStorage.getItem(ADMIN_EMAIL_STORAGE_KEY);
  return saved ? saved.trim().toLowerCase() : null;
};

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState(() => {
    const saved = sessionStorage.getItem("token");
    return saved && saved !== "undefined" ? JSON.parse(saved) : null;
  });

  const [authContext, setAuthContext] = useState(() => getAuthContextFromToken(authData));
  const [userEmail, setUserEmail] = useState(() => readStoredEmail());
  const freshLoginAtRef = useRef(0);

  const logout = useCallback(() => {
    setAuthData(null);
    setAuthContext(EMPTY_AUTH_CONTEXT);
    setUserEmail(null);
    sessionStorage.removeItem(ADMIN_EMAIL_STORAGE_KEY);
    sessionStorage.clear();
  }, []);

  useEffect(() => {
    const issuedAt = freshLoginAtRef.current;
    const handler = setTimeout(async () => {
      if (!authData || !authContext.role) {
        return;
      }
      if (Date.now() - issuedAt < FRESH_LOGIN_REFRESH_SKIP_MS) {
        return;
      }

      try {
        const refreshed = await refresh();
        if (refreshed?.token) {
          setAuthData(refreshed.token);
          setAuthContext(getAuthContextFromToken(refreshed.token));
        }
      } catch {
        logout();
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [authData, authContext.role, logout]);

  const login = useCallback(async (credentials) => {
    try {
      const data = await loginService(credentials);
      if (data?.token) {
        const normalizedEmail = credentials.email.trim().toLowerCase();
        freshLoginAtRef.current = Date.now();
        setAuthData(data.token);
        setUserEmail(normalizedEmail);
        sessionStorage.setItem("token", JSON.stringify(data.token));
        sessionStorage.setItem(ADMIN_EMAIL_STORAGE_KEY, normalizedEmail);
        const ctx = getAuthContextFromToken(data.token);
        setAuthContext(ctx);
        return { success: true, role: ctx.role };
      }
      return { success: false };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Accesso non riuscito. Riprova.";
      return { success: false, message };
    }
  }, []);

  const value = useMemo(
    () => ({
      user: authData,
      userEmail,
      role: authContext.role,
      loginId: authContext.loginId,
      isAdmin: authContext.role === "admin",
      login,
      logout,
    }),
    [authData, authContext, login, logout, userEmail]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
